<template>
  <teleport to="body">
    <div v-if="cible" class="pec-fond" @mousedown.self="fermer">
      <div class="pec-carte card shadow">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-heart text-danger me-2"></i>Prise en charge sociale</h5>
          <button type="button" class="btn-close" @click="fermer"></button>
        </div>
        <div class="card-body">
          <p class="small text-muted mb-3">
            {{ cible.libelle }}<template v-if="cible.montant"> — <strong>{{ fcfa(cible.montant) }}</strong></template>.
            <template v-if="cible.type === 'hospitalisation'">Un pourcentage suit le séjour jusqu'à la sortie ; un montant reste fixe. </template>
            <template v-if="cible.type === 'ordonnance'">Elle s'applique à la délivrance en pharmacie (pourcentage du montant vendu, ou montant maximum). </template>
            La clinique offre tout ou partie de cet acte au patient : rien n'entre en caisse pour la part offerte,
            qui apparaît à part dans les rapports. Le patient paie le reste à la caisse (sans assurance).
          </p>

          <label class="form-label small fw-semibold">Part offerte</label>
          <div class="d-flex flex-wrap gap-2 mb-2">
            <button v-for="p in [100, 75, 50, 25]" :key="p" type="button" class="btn btn-sm"
                    :class="mode === 'pct' && pourcentage === p ? 'btn-primary' : 'btn-outline-primary'" @click="mode = 'pct'; pourcentage = p">
              {{ p === 100 ? 'Totale (100 %)' : p + ' %' }}
            </button>
            <button type="button" class="btn btn-sm" :class="mode === 'montant' ? 'btn-primary' : 'btn-outline-primary'" @click="mode = 'montant'">Autre montant</button>
          </div>
          <div v-if="mode === 'montant'" class="input-group mb-2">
            <input v-model.number="montant" type="number" min="1" :max="cible.montant || null" class="form-control" :placeholder="cible.montant ? 'Montant offert' : 'Montant maximum offert'">
            <span class="input-group-text">FCFA</span>
          </div>
          <div v-if="cible.montant" class="small mb-3 p-2 rounded bg-light">
            Offert par la clinique : <strong>{{ fcfa(offert) }}</strong> · le patient paie : <strong>{{ fcfa(Math.max(0, cible.montant - offert)) }}</strong>
          </div>

          <label class="form-label small fw-semibold">Raison</label>
          <select v-model="categorie" class="form-select mb-2">
            <option v-for="(libelle, cle) in CATEGORIES" :key="cle" :value="cle">{{ libelle }}</option>
          </select>
          <textarea v-model="motif" class="form-control" rows="3" maxlength="255"
                    placeholder="Motif détaillé : situation du patient, qui a donné l'ordre (ex. « Ordre du promoteur, M. … : patient sans ressources »)"></textarea>

          <div class="small mt-3" :class="peutAccorder ? 'text-success' : 'text-muted'">
            <i class="bi me-1" :class="peutAccorder ? 'bi-check-circle' : 'bi-hourglass-split'"></i>
            <template v-if="peutAccorder">Vous êtes autorisé : la prise en charge est accordée dès l'enregistrement.</template>
            <template v-else>La demande est envoyée pour accord (promoteur, direction). Le patient attend la décision avant la caisse.</template>
          </div>
          <div v-if="resume && resume.plafond > 0" class="small mt-1" :class="resume.accorde + offert > resume.plafond ? 'text-danger' : 'text-muted'">
            Plafond du mois : {{ fcfa(resume.accorde) }} accordés sur {{ fcfa(resume.plafond) }}<span v-if="resume.accorde + offert > resume.plafond"> — cette prise en charge le dépasse : elle sera signalée.</span>
          </div>
          <div v-if="erreur" class="alert alert-danger py-2 small mt-3 mb-0">{{ erreur }}</div>
        </div>
        <div class="card-footer text-end">
          <button type="button" class="btn btn-outline-secondary me-2" @click="fermer">Retour</button>
          <button type="button" class="btn btn-danger" :disabled="envoi" @click="enregistrer">
            <span v-if="envoi" class="spinner-border spinner-border-sm me-1"></span>{{ peutAccorder ? 'Accorder' : 'Envoyer la demande' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'

export const CATEGORIES = {
  INDIGENT: 'Patient indigent',
  HUMANITAIRE: 'Cas humanitaire / urgence sociale',
  PERSONNEL: 'Personnel de la clinique ou sa famille',
  DECISION_DIRECTION: 'Décision du promoteur / de la direction',
  PARTENARIAT: 'Partenariat, campagne, association',
  AUTRE: 'Autre',
}

export const STATUTS_SOCIAL = {
  DEMANDEE: ['Prise en charge demandée', 'bg-warning text-dark'],
  ACCORDEE: ['Pris en charge (social)', 'bg-info text-dark'],
  REFUSEE: ['Prise en charge refusée', 'bg-secondary'],
  ANNULEE: ['Prise en charge annulée', 'bg-secondary'],
}

/** Prises en charge du patient, par consultation et par facture (les dernières en cours). */
export async function prisesEnChargeDuPatient(store, patientId) {
  if (!store.getters.hasAnyPermission(['SOCIAL_DEMANDER', 'SOCIAL_ACCORDER'])) return { consultations: {}, factures: {}, hospitalisations: {}, ordonnances: {}, peutAccorder: false }
  try {
    const res = (await api.liste('prises-en-charge-sociales', { patient: patientId, itemsperpage: 200, orderBy: 'p.demandeeLe', sortOrder: 'ASC' })).data
    const consultations = {}
    const factures = {}
    const hospitalisations = {}
    const ordonnances = {}
    for (const p of res.data || []) {
      if (p.consultation) consultations[p.consultation.id] = p
      if (p.facture) factures[p.facture.id] = p
      if (p.hospitalisation) hospitalisations[p.hospitalisation.id] = p
      if (p.ordonnance && !p.vente) ordonnances[p.ordonnance.id] = p
    }
    return { consultations, factures, hospitalisations, ordonnances, peutAccorder: !!res.peutAccorder, resume: res.resume }
  } catch (e) {
    return { consultations: {}, factures: {}, hospitalisations: {}, ordonnances: {}, peutAccorder: false }
  }
}

export default {
  name: 'PriseEnChargeSociale',
  props: {
    // { type: 'consultation' | 'facture', id, libelle, montant }
    cible: { type: Object, default: null },
    peutAccorder: Boolean,
    resume: { type: Object, default: null },
  },
  emits: ['fermer', 'enregistre'],
  data() {
    return { CATEGORIES, mode: 'pct', pourcentage: 100, montant: null, categorie: 'INDIGENT', motif: '', envoi: false, erreur: '' }
  },
  computed: {
    offert() {
      if (!this.cible) return 0
      if (this.mode === 'pct') return Math.round((this.cible.montant || 0) * this.pourcentage / 100)
      const m = Math.max(0, Number(this.montant) || 0)
      return this.cible.montant ? Math.min(m, this.cible.montant) : m
    },
  },
  watch: {
    cible(v) {
      if (v) Object.assign(this, { mode: 'pct', pourcentage: 100, montant: null, categorie: 'INDIGENT', motif: '', erreur: '' })
    },
  },
  methods: {
    fermer() { this.$emit('fermer') },
    async enregistrer() {
      this.erreur = ''
      if (this.motif.trim().length < 3) { this.erreur = 'Le motif est obligatoire.'; return }
      if (this.mode === 'montant' && this.offert <= 0) { this.erreur = 'Indiquez le montant offert.'; return }
      this.envoi = true
      try {
        const corps = { [this.cible.type]: this.cible.id, categorie: this.categorie, motif: this.motif.trim() }
        if (this.mode === 'pct') corps.pourcentage = this.pourcentage
        else corps.montant = this.offert
        const p = (await api.post('prises-en-charge-sociales', corps)).data
        notifier(p.statut === 'ACCORDEE'
          ? (p.horsPlafond ? 'Prise en charge accordée (au-delà du plafond du mois : elle est signalée).' : 'Prise en charge accordée.')
          : 'Demande envoyée pour accord.', p.horsPlafond ? 'warning' : 'success')
        this.$emit('enregistre', p)
      } catch (e) {
        this.erreur = messageErreur(e)
      } finally {
        this.envoi = false
      }
    },
    fcfa(v) { return new Intl.NumberFormat('fr-FR').format(v || 0) + ' FCFA' },
  },
}
</script>

<style scoped>
.pec-fond { position: fixed; inset: 0; background: rgba(15, 23, 42, .45); z-index: 2050; display: flex; align-items: flex-start; justify-content: center; padding: 6vh 16px; overflow-y: auto; }
.pec-carte { width: 100%; max-width: 540px; border-radius: 12px; border: none; }
</style>
